import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { FileReference } from '@/utils/temporaryFileStorage';
import { sendNotification } from '@/utils/sendNotification';

export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  category_id?: string;
  attachments?: FileReference[];
}

export const useContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitContactForm = async (formData: ContactFormData) => {
    setIsSubmitting(true);
    
    try {
      // Get category info for priority setting and auto-response
      let priority = 'medium';
      let categoryName = 'general inquiry';
      
      if (formData.category_id) {
        const { data: category } = await supabase
          .from('contact_categories')
          .select('priority_level, name')
          .eq('id', formData.category_id)
          .single();
        
        if (category) {
          priority = category.priority_level;
          categoryName = category.name;
        }
      }

      const { data, error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            subject: formData.subject,
            message: formData.message,
            category_id: formData.category_id,
            priority: priority as any,
            status: 'new',
            attachments: JSON.stringify(formData.attachments || [])
          }
        ])
        .select()
        .single();

      if (error) throw error;

      // Send auto-response email
      try {
        await sendNotification('contactAutoResponse', formData.email, {
          name: formData.name,
          subject: formData.subject,
          category_name: categoryName
        });
      } catch (emailError) {
        console.error('Failed to send auto-response email:', emailError);
        // Don't fail the form submission if email fails
      }

      toast.success('Thank you for your message! We will respond soon.');
      return { success: true, data };
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
      return { success: false, error };
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitContactForm,
    isSubmitting
  };
};